import { CreateCompanyEnrich } from "../schema/companyEnrich";

type Field = string | number | boolean | null | undefined | unknown[];

/** The collapsible sections of the Company Enrich source form, keyed by section. */
export type CompanyEnrichFilterSection =
	| "person"
	| "company"
	| "advancedSearch"
	| "funding"
	| "workforceGrowth"
	| "location"
	| "excludeCompanies";

export type CompanyEnrichSectionFields = Record<CompanyEnrichFilterSection, Field[]>;

export type CompanyEnrichSectionCounts = Record<CompanyEnrichFilterSection, number>;

/**
 * Which sections start expanded, plus the excluded-industries sub-control that lives
 * inside the company section behind its own toggle.
 */
export type VisibleCompanyEnrichSections = Record<CompanyEnrichFilterSection, boolean> & {
	excludeIndustries: boolean;
};

/** A field counts as an active filter when it holds a value — an empty array does not. */
export const countActiveFilters = (fields: Field[]): number =>
	fields.reduce<number>((count, field) => {
		if (field === null || field === undefined || field === "" || field === false) {
			return count;
		}

		if (Array.isArray(field)) {
			return count + (field.length > 0 ? 1 : 0);
		}

		return count + 1;
	}, 0);

/**
 * The single definition of which form fields belong to which section. Both the count
 * badges and the initial expanded/collapsed state read from here, so a filter can never
 * be counted by one and ignored by the other.
 */
export const getFilterFieldsBySection = (
	values: Partial<CreateCompanyEnrich>
): CompanyEnrichSectionFields => ({
	person: [
		values.query,
		values.positionQuery,
		values.seniority,
		values.department,
		values.education?.institutionNameQuery,
		values.countries,
		values.atCurrentCompanyAfter,
		values.atCurrentCompanyBefore,
		values.atCurrentPositionAfter,
		values.atCurrentPositionBefore,
	],
	company: [
		values.domains,
		values.companyFilterSearch?.industries,
		values.companyFilterSearch?.exclude?.industries,
		values.companyFilterSearch?.employees,
		values.companyFilterSearch?.revenue,
		values.companyFilterSearch?.category,
		values.companyFilterSearch?.technologies,
		values.companyFilterSearch?.type,
		values.companyFilterSearch?.foundedYear?.min,
		values.companyFilterSearch?.foundedYear?.max,
	],
	advancedSearch: [
		values.companyFilterSearch?.semanticQuery,
		values.companyFilterSimilar?.similarDomains,
		values.companyFilterSearch?.keywords,
	],
	funding: [
		values.companyFilterSearch?.fundingRounds,
		values.companyFilterSearch?.fundingAmount?.min,
		values.companyFilterSearch?.fundingAmount?.max,
		values.companyFilterSearch?.fundingYear?.min,
		values.companyFilterSearch?.fundingYear?.max,
	],
	workforceGrowth: [
		values.companyFilterSearch?.workforceGrowth?.period,
		values.companyFilterSearch?.workforceGrowth?.department,
		values.companyFilterSearch?.workforceGrowth?.minPercent,
		values.companyFilterSearch?.workforceGrowth?.maxPercent,
	],
	location: [
		values.companyFilterSearch?.regions,
		values.companyCountries,
		values.companyStates,
		values.companyCities,
	],
	excludeCompanies: [
		values.excludePositionQuery,
		values.excludeDepartment,
		values.excludeCountries,
		values.excludeCompanies?.scope,
		values.excludeCompanies?.listIds,
		values.excludeCompanies?.excludeCompetitors,
		values.excludeDomains,
	],
});

export const countFiltersBySection = (
	values: Partial<CreateCompanyEnrich>
): CompanyEnrichSectionCounts => {
	const fields = getFilterFieldsBySection(values);

	return {
		person: countActiveFilters(fields.person),
		company: countActiveFilters(fields.company),
		advancedSearch: countActiveFilters(fields.advancedSearch),
		funding: countActiveFilters(fields.funding),
		workforceGrowth: countActiveFilters(fields.workforceGrowth),
		location: countActiveFilters(fields.location),
		excludeCompanies: countActiveFilters(fields.excludeCompanies),
	};
};

const ALL_COLLAPSED: VisibleCompanyEnrichSections = {
	person: true,
	company: false,
	advancedSearch: false,
	funding: false,
	workforceGrowth: false,
	location: false,
	excludeCompanies: false,
	excludeIndustries: false,
};

/**
 * PREC-3868: a saved source whose filters sit in collapsed sections looks like a source with
 * no filters at all, while every search still applies them. Open the sections that actually
 * hold something, so what the form shows matches what the search sends.
 */
export const resolveVisibleFilterSections = (
	values?: Partial<CreateCompanyEnrich> | null
): VisibleCompanyEnrichSections => {
	if (!values) {
		return { ...ALL_COLLAPSED };
	}

	const counts = countFiltersBySection(values);

	return {
		person: true,
		company: counts.company > 0,
		advancedSearch: counts.advancedSearch > 0,
		funding: counts.funding > 0,
		workforceGrowth: counts.workforceGrowth > 0,
		location: counts.location > 0,
		excludeCompanies: counts.excludeCompanies > 0,
		excludeIndustries: (values.companyFilterSearch?.exclude?.industries?.length ?? 0) > 0,
	};
};

export const collapsedFilterSections = (): VisibleCompanyEnrichSections => ({ ...ALL_COLLAPSED });
